import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput';
import '../company/CompanyForm.css';
import './UnitsSection.css';
import {
  listUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  UNIT_TYPES,
  UNIT_STATUS_OPTIONS,
} from '../../../services/unitsApi';
import { listPublications } from '../../../services/publicationsApi';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import '../calendar/vendor/core/index.css';
import '../calendar/vendor/daygrid/index.css';
import { FaStickyNote } from 'react-icons/fa';
import {
  FiCheckCircle,
  FiTruck,
  FiGlobe,
  FiMoreVertical,
  FiClock,
  FiTool,
  FiHash,
  FiPackage,
  FiInfo,
  FiSearch,
} from 'react-icons/fi';
import {
  LuMapPin,
  LuShieldCheck,
  LuShieldPlus,
  LuArrowUpDown,
  LuTriangleAlert,
  LuSignal,
  LuUsers,
  LuSparkles,
} from 'react-icons/lu';
import useTableColumns from '../../../hooks/useTableColumns';
import TableColumnMenu from '../../../components/TableColumnMenu';
import UnitsGarageGrid from '../../operations/units/UnitsGarageGrid';

const CURRENCY_OPTIONS = [
  { value: 'MXN', label: 'MXN - Peso mexicano' },
  { value: 'USD', label: 'USD - Dolar estadounidense' },
];

const DESTINATION_CATALOG = [
  { code: 'MXGTO', label: 'Guanajuato, GTO', description: 'Puerto interior' },
  { code: 'MXSLP', label: 'San Luis Potosi, SLP', description: 'Zona industrial' },
  { code: 'MXQRO', label: 'Queretaro, QRO', description: 'El Marques' },
  { code: 'MXMTY', label: 'Monterrey, NL', description: 'Apodaca y zona metropolitana' },
  { code: 'MXMEX', label: 'Ciudad de Mexico, CDMX', description: 'Valle de Mexico' },
  { code: 'MXGDL', label: 'Guadalajara, JAL', description: 'El Salto' },
  { code: 'MXTLC', label: 'Toluca, MEX', description: 'Lerma' },
  { code: 'MXVER', label: 'Veracruz, VER', description: 'Zona portuaria' },
  { code: 'MXTIJ', label: 'Tijuana, BCN', description: 'Mesa de Otay' },
  { code: 'MXMID', label: 'Merida, YUC', description: 'Zona industrial' },
];

const destinationDisplay = entry => `${entry.label} - ${entry.code}`;

const getDestinationLabel = code => {
  if (!code) return '';
  const match = DESTINATION_CATALOG.find(option => option.code === code);
  return match ? destinationDisplay(match) : code;
};

const UNLOCODE_PATTERN = /\b([A-Z]{2}[A-Z0-9]{3})\b/;

const matchDestinationCode = inputValue => {
  if (!inputValue) return '';
  const trimmed = inputValue.trim();
  const codeMatch = trimmed.toUpperCase().match(UNLOCODE_PATTERN);
  if (codeMatch) {
    return codeMatch[1];
  }
  const normalized = trimmed.toLowerCase();
  const exact = DESTINATION_CATALOG.find(
    option =>
      option.code.toLowerCase() === normalized || destinationDisplay(option).toLowerCase() === normalized
  );
  if (exact) return exact.code;
  const partial = DESTINATION_CATALOG.find(option =>
    option.label.toLowerCase().includes(normalized)
  );
  return partial?.code || '';
};

const DUPLICATE_UNLOCODE_ERROR =
  'Ese UN/LOCODE ya esta agregado. Elimina el duplicado o usa otro destino.';

const DEFAULT_CURRENCY = CURRENCY_OPTIONS[0].value;

const initialFormState = {
  id: null,
  tractorType: '',
  model: '',
  capacity: '',
  economicNumber: '',
  status: 'available',
  currency: DEFAULT_CURRENCY,
  basePrice: '',
  destinationPrices: [],
  liftGate: false,
  gps: false,
  insured: false,
  cTpat: false,
  eTracking: false,
  armored: false,
  teamDrivers: false,
  hazmat: false,
  customEquipment: [],
  extraAttributes: [],
  photoFile: null,
  photoPreview: '',
  clearPhoto: false,
  availabilityRanges: [],
};

const EQUIPMENT_OPTIONS = [
  { name: 'liftGate', label: 'Lift gate' },
  { name: 'gps', label: 'GPS' },
  { name: 'insured', label: 'Asegurado' },
  { name: 'cTpat', label: 'C-TPAT' },
  { name: 'eTracking', label: 'E-Tracking' },
  { name: 'armored', label: 'Unidades blindadas' },
  { name: 'teamDrivers', label: 'Operador dual' },
  { name: 'hazmat', label: 'Hazmat cert.' },
];

const STATUS_SEGMENTS = [
  {
    value: 'available',
    label: 'Disponible',
    description: 'Lista para asignar viaje',
    tone: 'success',
    Icon: FiCheckCircle,
  },
  {
    value: 'reserved',
    label: 'Reservada',
    description: 'En ruta o comprometida',
    tone: 'warning',
    Icon: FiClock,
  },
  {
    value: 'maintenance',
    label: 'Mantenimiento',
    description: 'En taller o inspeccion',
    tone: 'danger',
    Icon: FiTool,
  },
];

const EQUIPMENT_ICON_MAP = {
  gps: LuMapPin,
  liftGate: LuArrowUpDown,
  insured: LuShieldCheck,
  cTpat: LuShieldPlus,
  eTracking: LuSignal,
  armored: LuShieldPlus,
  teamDrivers: LuUsers,
  hazmat: LuTriangleAlert,
};

const MAX_CUSTOM_EQUIPMENT = 8;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const UNITS_COLUMN_DEFAULTS = [
  { key: 'unit', label: 'Unidad', width: 280, minWidth: 220 },
  { key: 'status', label: 'Estado', width: 150, minWidth: 130 },
  { key: 'activity', label: 'Ultima actividad', width: 220, minWidth: 200 },
  { key: 'createdBy', label: 'Creada por', width: 200, minWidth: 170 },
  { key: 'updatedAt', label: 'Actualizado', width: 150, minWidth: 130 },
  { key: 'actions', label: '', width: 160, minWidth: 140 },
];

const UNIT_STATUS_LABELS = UNIT_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const FLEET_KPI_STORAGE_KEY = 'flotte:fleetKpiCollapsed';

const formatCurrencyDisplay = value => {
  if (value === '' || value === null || typeof value === 'undefined') return '';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '';
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNumberForInput = value => {
  if (value === '' || value === null || typeof value === 'undefined') return '';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '';
  const fixed = numeric.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
};

const parseCurrencyInput = raw => {
  if (typeof raw !== 'string') raw = String(raw ?? '');
  const cleaned = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '');
  if (!cleaned) return '';
  const trimmed = raw.trim();
  const endsWithDecimal = trimmed.endsWith('.');
  const [integerPart = '', ...decimalParts] = cleaned.split('.');
  let integerDigits = integerPart;
  let decimalDigits = decimalParts.join('');
  if (decimalDigits.length > 2) {
    decimalDigits = decimalDigits.slice(0, 2);
  }
  integerDigits = integerDigits.replace(/^0+(?=\d)/, '');
  if (!integerDigits && decimalDigits) {
    integerDigits = '0';
  }
  if (!integerDigits && !decimalDigits) {
    return '';
  }
  if (endsWithDecimal && decimalDigits.length === 0) {
    return `${integerDigits || '0'}.`;
  }
  return decimalDigits.length ? `${integerDigits || '0'}.${decimalDigits}` : integerDigits;
};

const buildPriceLabel = (currency, amount) => {
  if (amount === null || typeof amount === 'undefined' || amount === '') {
    return 'Consulta precio';
  }
  return `${currency} ${formatCurrencyDisplay(amount)}`;
};

const parseISOToDate = isoDate => {
  if (!DATE_REGEX.test(isoDate)) return null;
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toISODateString = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const extractDatePortion = value => {
  if (!value) return '';
  if (DATE_REGEX.test(value)) return value;
  return String(value).slice(0, 10);
};

const addDaysToISO = (isoDate, amount) => {
  if (!DATE_REGEX.test(isoDate)) return '';
  const date = parseISOToDate(isoDate);
  if (!date) return '';
  date.setDate(date.getDate() + amount);
  return toISODateString(date);
};

const normalizeRange = (rawStart, rawEnd) => {
  const start = extractDatePortion(rawStart);
  if (!DATE_REGEX.test(start)) return null;
  const tentativeEnd = extractDatePortion(rawEnd || rawStart);
  if (!DATE_REGEX.test(tentativeEnd)) {
    return { start, end: start };
  }
  if (tentativeEnd < start) {
    return { start: tentativeEnd, end: start };
  }
  return { start, end: tentativeEnd };
};

const sortRanges = ranges =>
  [...ranges].sort((a, b) => {
    if (a.start === b.start) {
      const endA = a.end || a.start;
      const endB = b.end || b.start;
      return endA.localeCompare(endB);
    }
    return a.start.localeCompare(b.start);
  });

const splitIntoSingleDayRanges = normalizedRange => {
  if (!normalizedRange) return [];
  const startDate = parseISOToDate(normalizedRange.start);
  const endDate = parseISOToDate(normalizedRange.end);
  if (!startDate || !endDate) return [];
  const result = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const iso = toISODateString(cursor);
    if (!iso) break;
    result.push({ start: iso, end: iso });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const RELATIVE_TIME_DIVISIONS = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

const formatRelativeTimeFromNow = (timestamp, formatter) => {
  if (!timestamp || !formatter) return '';
  const target = new Date(timestamp);
  if (Number.isNaN(target.getTime())) return '';
  let duration = (target.getTime() - Date.now()) / 1000;
  for (const division of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return '';
};

const getRouteLabel = unit => {
  if (!unit || !Array.isArray(unit.destinationPrices) || unit.destinationPrices.length === 0) {
    return '';
  }
  const [first, second] = unit.destinationPrices;
  const labelForEntry = entry =>
    entry?.destinationLabel || entry?.label || entry?.destinationCode || '';
  if (first && second) {
    const origin = labelForEntry(first);
    const destination = labelForEntry(second);
    if (origin && destination) {
      return `${origin} → ${destination}`;
    }
  }
  return labelForEntry(first) || labelForEntry(second) || '';
};

export default function UnitsSection({ user, empresaId }) {
  const [form, setForm] = useState(initialFormState);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState('list');
  const [listDisplayMode, setListDisplayMode] = useState('table');
  const [customEquipmentInput, setCustomEquipmentInput] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ key: 'unit', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const customEquipmentLimitReached = (form.customEquipment?.length || 0) >= MAX_CUSTOM_EQUIPMENT;
  const creatorId = user?.id || '';
  const [activeColumnMenu, setActiveColumnMenu] = useState('');
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(FLEET_KPI_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const previewRef = useRef('');
  const columnHeaderRefs = useRef({});
  const columnResizeStateRef = useRef({
    columnKey: '',
    startX: 0,
    startWidth: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const relativeTimeFormatter = useMemo(
    () =>
      typeof Intl !== 'undefined'
        ? new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
        : null,
    []
  );

  const {
    columns: unitColumns,
    visibleColumns: visibleUnitColumns,
    moveColumn: moveUnitColumn,
    toggleColumn: toggleUnitColumn,
    setColumnWidth: setUnitColumnWidth,
    resetColumns: resetUnitColumns,
  } = useTableColumns(`units.${empresaId || creatorId || 'default'}`, UNITS_COLUMN_DEFAULTS);

  const hasPreview = useMemo(() => Boolean(form.photoPreview), [form.photoPreview]);

  const toggleColumnMenu = useCallback(columnKey => {
    setActiveColumnMenu(prev => (prev === columnKey ? '' : columnKey));
  }, []);

  const handleColumnContextMenu = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      toggleColumnMenu(columnKey);
    },
    [toggleColumnMenu]
  );

  const handleColumnSort = useCallback(columnKey => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const handleColumnHeaderKeyDown = useCallback(
    (event, columnKey) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleColumnSort(columnKey);
        return;
      }
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault();
        toggleColumnMenu(columnKey);
      }
    },
    [handleColumnSort, toggleColumnMenu]
  );

  const getSortValue = useCallback((unit, columnKey) => {
    if (!unit) return '';
    switch (columnKey) {
      case 'unit': {
        const label =
          unit.model ||
          unit.tractorType ||
          unit.economicNumber ||
          unit.plates ||
          unit.id ||
          '';
        return label.toString().toLowerCase();
      }
      case 'status': {
        const statusValue = unit.status || '';
        const label = UNIT_STATUS_LABELS[statusValue] || statusValue || '';
        return label.toString().toLowerCase();
      }
      case 'createdBy': {
        const label =
          unit.createdByUser?.name ||
          unit.createdByUser?.username ||
          unit.createdByUser?.email ||
          '';
        return label.toString().toLowerCase();
      }
      case 'activity': {
        const dateValue = unit.updated || unit.created;
        const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      case 'updatedAt': {
        const dateValue = unit.updated || unit.created;
        const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      default:
        return '';
    }
  }, []);

  const beginColumnResize = useCallback(
    (clientX, column) => {
      if (typeof clientX !== 'number') return;
      columnResizeStateRef.current = {
        columnKey: column.key,
        startX: clientX,
        startWidth: column.width || column.minWidth || 180,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.add('is-resizing-column');
      }
    },
    []
  );

  const handleColumnResizeMouseDown = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.clientX, column);
    },
    [beginColumnResize]
  );

  const handleColumnResizeTouchStart = useCallback(
    (event, column) => {
      if (!event.touches || event.touches.length !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.touches[0].clientX, column);
    },
    [beginColumnResize]
  );

  const handlePageSizeChange = useCallback(event => {
    setPageSize(Number(event.target.value));
  }, []);

  useEffect(() => {
    const stopResizing = () => {
      if (!columnResizeStateRef.current.columnKey) return;
      columnResizeStateRef.current = {
        columnKey: '',
        startX: 0,
        startWidth: 0,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.remove('is-resizing-column');
      }
    };

    const handlePointerMove = event => {
      const { columnKey, startWidth, startX } = columnResizeStateRef.current;
      if (!columnKey) return;
      const clientX =
        'touches' in event ? event.touches?.[0]?.clientX : event.clientX;
      if (typeof clientX !== 'number') return;
      event.preventDefault();
      const delta = clientX - startX;
      setUnitColumnWidth(columnKey, startWidth + delta);
    };

    const handlePointerUp = event => {
      if (!columnResizeStateRef.current.columnKey) return;
      if (event) {
        event.preventDefault();
      }
      stopResizing();
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    window.addEventListener('blur', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
      window.removeEventListener('blur', stopResizing);
      stopResizing();
    };
  }, [setUnitColumnWidth]);

  const resetPreview = () => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = '';
    }
  };

  const resetForm = () => {
    resetPreview();
    setForm({ ...initialFormState });
    setError('');
    setCustomEquipmentInput('');
    setActiveTab('general');
  };

  const loadUnits = async () => {
    if (!empresaId && !creatorId) {
      setUnits([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (empresaId) {
        params.companyId = empresaId;
      } else if (creatorId) {
        params.createdBy = creatorId;
      }
      const items = await listUnits(params);
      setUnits(items);
    } catch (err) {
      setError(err?.message || 'No pudimos cargar las unidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, empresaId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(FLEET_KPI_STORAGE_KEY, kpiCollapsed ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, [kpiCollapsed]);

  useEffect(() => {
    if (!empresaId && !creatorId) {
      setPublicationSnapshot({ total: 0, unitIds: [] });
      return undefined;
    }
    let ignore = false;
    const fetchPublications = async () => {
      try {
        const params = empresaId ? { companyId: empresaId } : { creatorId };
        const items = await listPublications(params);
        if (ignore) return;
        const unitIds = Array.from(
          new Set(
            items
              .map(item => item.unitId || item.unit || '')
              .filter(Boolean)
          )
        );
        setPublicationSnapshot({ total: items.length, unitIds });
      } catch (err) {
        if (!ignore) {
          console.error('No pudimos recuperar las publicaciones asociadas.', err);
          setPublicationSnapshot({ total: 0, unitIds: [] });
        }
      }
    };
    fetchPublications();
    return () => {
      ignore = true;
    };
  }, [empresaId, creatorId]);

  useEffect(() => {
    if (mode !== 'list') {
      setActiveColumnMenu('');
    }
  }, [mode]);

  useEffect(
    () => () => {
      resetPreview();
    },
    []
  );

  const handleFieldChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setSuccess('');
  };

  const handleStatusChange = value => {
    setForm(prev => ({
      ...prev,
      status: value,
    }));
    setError('');
    setSuccess('');
  };

  const handleCheckboxChange = event => {
    const { name, checked } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: checked,
    }));
    setSuccess('');
  };

  const handleBasePriceChange = value => {
    const parsed = parseCurrencyInput(value);
    setForm(prev => ({
      ...prev,
      basePrice: parsed,
    }));
    setSuccess('');
  };

  const handlePublishShortcut = useCallback(
    unit => {
      if (!unit?.id) return;
      const params = new URLSearchParams(location.search || '');
      params.set('section', 'publicaciones');
      params.set('unitId', unit.id);
      navigate(`/dashboard?${params.toString()}`);
    },
    [location.search, navigate]
  );

  const handleDestinationPriceChange = (id, value) => {
    const parsed = parseCurrencyInput(value);
    setForm(prev => ({
      ...prev,
      destinationPrices: (prev.destinationPrices || []).map(entry =>
        entry.id === id ? { ...entry, price: parsed } : entry
      ),
    }));
  };

  const handleDestinationInputChange = (id, rawValue) => {
    const matchedCode = matchDestinationCode(rawValue);
    setForm(prev => {
      const duplicate =
        matchedCode &&
        (prev.destinationPrices || []).some(
          entry => entry.id !== id && entry.destinationCode === matchedCode
        );
      if (duplicate) {
        setError(DUPLICATE_UNLOCODE_ERROR);
      } else if (error === DUPLICATE_UNLOCODE_ERROR) {
        setError('');
      }
      return {
        ...prev,
        destinationPrices: (prev.destinationPrices || []).map(entry => {
          if (entry.id !== id) {
            return entry;
          }
          const isSameAsCurrent = entry.destinationCode === matchedCode;
          const nextCode =
            matchedCode && (!duplicate || isSameAsCurrent) ? matchedCode : '';
          return {
            ...entry,
            destinationCode: nextCode,
            destinationInput: rawValue,
          };
        }),
      };
    });
  };

  const handleDestinationAdd = () => {
    setForm(prev => ({
      ...prev,
      destinationPrices: [
        ...(prev.destinationPrices || []),
        {
          id: crypto.randomUUID(),
          destinationCode: '',
          destinationInput: '',
          price: '',
        },
      ],
    }));
    if (error === DUPLICATE_UNLOCODE_ERROR) {
      setError('');
    }
  };

  const handleDestinationRemove = id => {
    setForm(prev => ({
      ...prev,
      destinationPrices: (prev.destinationPrices || []).filter(entry => entry.id !== id),
    }));
    if (error === DUPLICATE_UNLOCODE_ERROR) {
      setError('');
    }
  };

  const availabilityDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    []
  );

  const formatAvailabilityRange = useCallback(
    range => {
      if (!range?.start) return '';
      const normalized = normalizeRange(range.start, range.end);
      if (!normalized) return '';
      const startDate = parseISOToDate(normalized.start);
      if (!startDate) return normalized.start;
      const startLabel = availabilityDateFormatter.format(startDate);
      const endDate = parseISOToDate(normalized.end);
      if (!endDate || normalized.end === normalized.start) {
        return startLabel;
      }
      const endLabel = availabilityDateFormatter.format(endDate);
      return `${startLabel} - ${endLabel}`;
    },
    [availabilityDateFormatter]
  );

  const availabilityEvents = useMemo(() => {
    if (!Array.isArray(form.availabilityRanges)) return [];
    return form.availabilityRanges
      .map((range, index) => {
        const normalized = normalizeRange(range?.start, range?.end);
        if (!normalized) return null;
        const exclusiveEnd = addDaysToISO(normalized.end, 1) || addDaysToISO(normalized.start, 1);
        if (!exclusiveEnd) return null;
        return {
          id: `availability-${index}-${normalized.start}-${normalized.end}`,
          title: 'Disponible',
          start: normalized.start,
          end: exclusiveEnd,
          allDay: true,
          classNames: ['units-availability-event'],
        };
      })
      .filter(Boolean);
  }, [form.availabilityRanges]);

  const handleAvailabilitySelect = useCallback(
    selectionInfo => {
      const start = extractDatePortion(selectionInfo.startStr);
      if (!DATE_REGEX.test(start)) return;
      const rawEndExclusive = extractDatePortion(selectionInfo.endStr);
      const inclusiveEnd =
        selectionInfo.allDay && rawEndExclusive
          ? addDaysToISO(rawEndExclusive, -1)
          : rawEndExclusive || start;
      const normalized = normalizeRange(start, inclusiveEnd);
      if (!normalized) return;
      const singleDayRanges = splitIntoSingleDayRanges(normalized);
      if (singleDayRanges.length === 0) return;
      setForm(prev => {
        const current = Array.isArray(prev.availabilityRanges)
          ? prev.availabilityRanges
          : [];
        const next = [...current];
        const seen = new Set(
          next.map(range => `${range.start}-${range.end || range.start}`)
        );
        let added = false;
        singleDayRanges.forEach(range => {
          const key = `${range.start}-${range.end}`;
          if (seen.has(key)) return;
          seen.add(key);
          next.push(range);
          added = true;
        });
        if (!added) {
          return prev;
        }
        return { ...prev, availabilityRanges: sortRanges(next) };
      });
      setError('');
      setSuccess('');
      selectionInfo.view.calendar.unselect();
    },
    [setError, setForm, setSuccess]
  );

  const handleAvailabilityEventClick = useCallback(
    info => {
      info.jsEvent?.preventDefault();
      const start = extractDatePortion(info.event.startStr);
      if (!DATE_REGEX.test(start)) return;
      const rawEndExclusive = extractDatePortion(info.event.endStr);
      const inclusiveEnd =
        info.event.allDay && rawEndExclusive
          ? addDaysToISO(rawEndExclusive, -1)
          : rawEndExclusive || start;
      const normalized = normalizeRange(start, inclusiveEnd);
      if (!normalized) return;
      setForm(prev => {
        const current = Array.isArray(prev.availabilityRanges)
          ? prev.availabilityRanges
          : [];
        const next = current.filter(
          range =>
            !(
              range.start === normalized.start &&
              (range.end || range.start) === normalized.end
            )
        );
        return { ...prev, availabilityRanges: next };
      });
      setError('');
      setSuccess('');
    },
    [setError, setForm, setSuccess]
  );

  const handleAvailabilityEventUpdate = useCallback(
    info => {
      const newStart = extractDatePortion(info.event.startStr);
      if (!DATE_REGEX.test(newStart)) {
        info.revert();
        return;
      }
      const newRawEndExclusive = extractDatePortion(info.event.endStr);
      const newInclusiveEnd =
        info.event.allDay && newRawEndExclusive
          ? addDaysToISO(newRawEndExclusive, -1)
          : newRawEndExclusive || newStart;
      const newRange = normalizeRange(newStart, newInclusiveEnd);
      if (!newRange) {
        info.revert();
        return;
      }
      const referenceEvent = info.oldEvent || info.prevEvent;
      const oldStart = extractDatePortion(referenceEvent?.startStr);
      if (!DATE_REGEX.test(oldStart)) {
        info.revert();
        return;
      }
      const oldRawEndExclusive = extractDatePortion(referenceEvent?.endStr);
      const oldInclusiveEnd =
        referenceEvent?.allDay && oldRawEndExclusive
          ? addDaysToISO(oldRawEndExclusive, -1)
          : oldRawEndExclusive || oldStart;
      const oldRange = normalizeRange(oldStart, oldInclusiveEnd);
      if (!oldRange) {
        info.revert();
        return;
      }
      const newSingleDayRanges = splitIntoSingleDayRanges(newRange);
      if (newSingleDayRanges.length === 0) {
        info.revert();
        return;
      }

      setForm(prev => {
        const current = Array.isArray(prev.availabilityRanges)
          ? prev.availabilityRanges
          : [];
        const base = current.filter(
          range =>
            !(
              range.start === oldRange.start &&
              (range.end || range.start) === oldRange.end
            )
        );
        const seen = new Set(
          base.map(range => `${range.start}-${range.end || range.start}`)
        );
        newSingleDayRanges.forEach(range => {
          const key = `${range.start}-${range.end}`;
          if (seen.has(key)) return;
          seen.add(key);
          base.push(range);
        });
        const next = sortRanges(base);
        return { ...prev, availabilityRanges: next };
      });
      setError('');
      setSuccess('');
    },
    [setError, setForm, setSuccess]
  );


  const handleCustomEquipmentAdd = () => {
    const rawValue = customEquipmentInput.trim();
    if (!rawValue) return;
    let added = false;
    setForm(prev => {
      if (prev.customEquipment.length >= MAX_CUSTOM_EQUIPMENT) {
        return prev;
      }
      const exists = prev.customEquipment.some(
        item => item.localeCompare(rawValue, undefined, { sensitivity: 'accent' }) === 0
      );
      if (exists) {
        return prev;
      }
      added = true;
      return {
        ...prev,
        customEquipment: [...prev.customEquipment, rawValue],
      };
    });
    if (added) {
      setCustomEquipmentInput('');
    }
    setSuccess('');
  };

  const handleCustomEquipmentRemove = index => {
    setForm(prev => ({
      ...prev,
      customEquipment: prev.customEquipment.filter((_, idx) => idx !== index),
    }));
    setSuccess('');
  };

  const handleAvailabilityRemove = index => {
    setForm(prev => {
      const next = Array.isArray(prev.availabilityRanges)
        ? prev.availabilityRanges.filter((_, itemIndex) => itemIndex !== index)
        : [];
      return {
        ...prev,
        availabilityRanges: next,
      };
    });
    setError('');
    setSuccess('');
  };

  const handlePhotoChange = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Selecciona una imagen valida.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen debe pesar maximo 5 MB.');
      return;
    }
    resetPreview();
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setForm(prev => ({
      ...prev,
      photoFile: file,
      photoPreview: url,
      clearPhoto: false,
    }));
    setError('');
    setSuccess('');
  };

  const handlePhotoRemove = () => {
    resetPreview();
    setForm(prev => ({
      ...prev,
      photoFile: null,
      photoPreview: '',
      clearPhoto: true,
    }));
  };

  const handleCreateNew = () => {
    resetForm();
    setSuccess('');
    setMode('form');
  };

  const handleEdit = unit => {
    resetPreview();
    const rawDestinations =
      (unit?.pricing && Array.isArray(unit.pricing.destinations)
        ? unit.pricing.destinations
        : Array.isArray(unit?.destinationPrices)
        ? unit.destinationPrices
        : []) || [];
    const normalizedDestinations = rawDestinations
      .map(entry => {
        const destinationCode = entry?.destinationCode || entry?.code || '';
        if (!destinationCode) return null;
        const priceValue =
          typeof entry?.price === 'number'
            ? entry.price
            : typeof entry?.amount === 'number'
            ? entry.amount
            : '';
        const destinationInput =
          typeof entry?.destinationInput === 'string' && entry.destinationInput.trim()
            ? entry.destinationInput
            : getDestinationLabel(destinationCode);
        return {
          id: entry?.id || entry?.uid || crypto.randomUUID(),
          destinationCode,
          destinationInput,
          price: formatNumberForInput(priceValue),
        };
      })
      .filter(Boolean);
    const basePriceValue =
      typeof unit?.pricing?.base === 'number'
        ? unit.pricing.base
        : typeof unit?.unitPrice === 'number'
        ? unit.unitPrice
        : typeof unit?.price === 'number'
        ? unit.price
        : '';
    const currencyValue = unit?.pricing?.currency || unit?.currency || DEFAULT_CURRENCY;
    const normalizedCustomEquipment = Array.from(
      new Set(
        (Array.isArray(unit.customEquipment) ? unit.customEquipment : [])
          .map(item => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
      )
    );
    setForm({
      id: unit.id,
      tractorType: unit.tractorType,
      model: unit.model,
      capacity: unit.capacity,
      economicNumber: unit.economicNumber || '',
      status: unit.status || 'available',
      currency: currencyValue,
      basePrice: formatNumberForInput(basePriceValue),
      destinationPrices: normalizedDestinations,
      liftGate: unit.liftGate,
      gps: unit.gps,
      insured: unit.insured,
      cTpat: unit.cTpat,
      eTracking: unit.eTracking,
      armored: unit.armored,
      teamDrivers: unit.teamDrivers,
      hazmat: unit.hazmat,
      customEquipment: normalizedCustomEquipment,
      extraAttributes: Array.isArray(unit.extraAttributes) ? unit.extraAttributes : [],
      photoFile: null,
      photoPreview: unit.photoUrl || '',
      clearPhoto: false,
      availabilityRanges: Array.isArray(unit.availabilityRanges)
        ? unit.availabilityRanges
        : [],
    });
    setError('');
    setSuccess('');
    setMode('form');
    setCustomEquipmentInput('');
    setActiveTab('general');
  };

  const handleDelete = async id => {
    if (!window.confirm('Eliminar esta unidad?')) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await deleteUnit(id);
      if (form.id === id) {
        resetForm();
        setMode('list');
      }
      setSuccess('Unidad eliminada.');
      await loadUnits();
    } catch (err) {
      setError(err?.message || 'No pudimos eliminar la unidad.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!creatorId) {
      setError('No se reconoce al usuario actual.');
      return;
    }
    if (!form.tractorType) {
      setError('El tipo de tractor es obligatorio.');
      return;
    }
    if (!form.model) {
      setError('Especifica el modelo.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    const normalizedCustomEquipment = Array.from(
      new Set(
        (form.customEquipment || [])
          .map(item => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
      )
    );

    const basePriceNumber =
      form.basePrice === '' || form.basePrice === null
        ? null
        : Number(form.basePrice);
    const seenDestinationCodes = new Set();
    for (const entry of form.destinationPrices || []) {
      if (!entry?.destinationCode || entry.price === '' || entry.price === null) {
        continue;
      }
      if (seenDestinationCodes.has(entry.destinationCode)) {
        setSaving(false);
        setError(DUPLICATE_UNLOCODE_ERROR);
        return;
      }
      seenDestinationCodes.add(entry.destinationCode);
    }
    const destinationPayload = (form.destinationPrices || [])
      .filter(entry => entry.destinationCode && entry.price !== '' && entry.price !== null)
      .map(entry => ({
        id: entry.id,
        destinationCode: entry.destinationCode,
        price: Number(entry.price),
      }));

    const payload = {
      tractorType: form.tractorType,
      model: form.model,
      capacity: form.capacity,
      economicNumber: form.economicNumber?.trim(),
      currency: form.currency,
      liftGate: form.liftGate,
      gps: form.gps,
      insured: form.insured,
      cTpat: form.cTpat,
      eTracking: form.eTracking,
      armored: form.armored,
      teamDrivers: form.teamDrivers,
      hazmat: form.hazmat,
      customEquipment: normalizedCustomEquipment,
      extraAttributes: form.extraAttributes,
      availabilityRanges: form.availabilityRanges,
      pricing: {
        currency: form.currency,
        base: basePriceNumber,
        destinations: destinationPayload,
      },
      unitPrice: basePriceNumber,
      priceLabel: buildPriceLabel(form.currency, basePriceNumber),
      destinationPrices: destinationPayload,
      company: empresaId || '',
      clearPhoto: form.clearPhoto,
    };

    if (form.photoFile instanceof File) {
      payload.photoFile = form.photoFile;
    }

    if (!form.id && creatorId) {
      payload.createdBy = creatorId;
    }

    try {
      if (form.id) {
        await updateUnit(form.id, payload);
        setSuccess('Unidad actualizada.');
      } else {
        await createUnit(payload);
        setSuccess('Unidad registrada.');
      }
      resetForm();
      setMode('list');
      await loadUnits();
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la unidad.');
    } finally {
      setSaving(false);
    }
  };

  const filteredUnits = useMemo(() => {
    if (!Array.isArray(units)) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return units;
    return units.filter(unit => {
      const fields = [
        unit.model,
        unit.tractorType,
        unit.nickname,
        unit.capacity,
        unit.economicNumber,
        unit.plates,
        unit.status,
        getRouteLabel(unit),
        unit.createdByUser?.name,
        unit.createdByUser?.username,
        unit.createdByUser?.email,
      ];
      return fields.some(value => {
        if (value === null || typeof value === 'undefined') return false;
        return value.toString().toLowerCase().includes(query);
      });
    });
  }, [units, searchQuery]);

  const sortedUnits = useMemo(() => {
    if (!Array.isArray(filteredUnits)) return [];
    const next = [...filteredUnits];
    if (!sortConfig?.key) return next;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      const valueA = getSortValue(a, sortConfig.key);
      const valueB = getSortValue(b, sortConfig.key);
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA === valueB) return 0;
        return valueA > valueB ? direction : -direction;
      }
      const stringA = valueA?.toString().toLowerCase() || '';
      const stringB = valueB?.toString().toLowerCase() || '';
      const comparison = stringA.localeCompare(stringB, 'es', { sensitivity: 'base' });
      if (comparison === 0) return 0;
      return comparison > 0 ? direction : -direction;
    });
    return next;
  }, [filteredUnits, sortConfig, getSortValue]);

  const totalPages = Math.max(1, Math.ceil(sortedUnits.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [pageSize, sortConfig]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const paginatedUnits = useMemo(() => {
    if (loading) return [];
    const start = page * pageSize;
    return sortedUnits.slice(start, start + pageSize);
  }, [loading, page, pageSize, sortedUnits]);
  const pageNumbers = useMemo(() => Array.from({ length: totalPages }, (_, index) => index), [totalPages]);

  const searchActive = Boolean(searchQuery.trim());
  const showEmptyState = !loading && units.length === 0;
  const showSearchNoResults = !loading && searchActive && filteredUnits.length === 0;
  const renderPlaceholderRow = useCallback(
    key => (
      <tr key={`placeholder-${key}`} className="table-placeholder-empty">
        <td colSpan={Math.max(1, visibleUnitColumns.length)} aria-hidden="true">
          &nbsp;
        </td>
      </tr>
    ),
    [visibleUnitColumns.length]
  );

  const [publicationSnapshot, setPublicationSnapshot] = useState({ total: 0, unitIds: [] });
  const publishedUnitIdsSet = useMemo(
    () => new Set(publicationSnapshot.unitIds || []),
    [publicationSnapshot.unitIds]
  );

  const destinationPricesList = Array.isArray(form.destinationPrices)
    ? form.destinationPrices
    : [];
  const availabilityRangesList = Array.isArray(form.availabilityRanges)
    ? form.availabilityRanges
    : [];
  const normalizedStatus = STATUS_SEGMENTS.some(segment => segment.value === form.status)
    ? form.status || STATUS_SEGMENTS[0].value
    : STATUS_SEGMENTS[0].value;
  const equipmentSelections = EQUIPMENT_OPTIONS.map(option => Boolean(form[option.name]));
  const equipmentProgress = (() => {
    if (!EQUIPMENT_OPTIONS.length) return 0;
    const selected = equipmentSelections.filter(Boolean).length;
    return Math.min(1, selected / EQUIPMENT_OPTIONS.length);
  })();
  const generalProgress = (() => {
    const baseFields = [form.tractorType, form.model, form.capacity, form.economicNumber];
    const completed = baseFields.filter(value => value && String(value).trim()).length;
    const statusScore = normalizedStatus ? 1 : 0;
    return Math.min(1, (completed + statusScore) / (baseFields.length + 1));
  })();
  const mediaProgress = form.photoPreview || form.photoFile ? 1 : 0;
  const pricingProgress = Math.min(
    1,
    ((form.basePrice ? 1 : 0) + (destinationPricesList.length ? 1 : 0)) / 2
  );
  const availabilityProgress = Math.min(1, availabilityRangesList.length / 3);
  const stepProgress = {
    general: generalProgress,
    equipment: equipmentProgress,
    media: mediaProgress,
    pricing: pricingProgress,
    availability: availabilityProgress,
  };

  const availableUnitsCount = useMemo(
    () => units.filter(unit => (unit.status || 'available') === 'available').length,
    [units]
  );
  const reservedUnitsCount = useMemo(
    () => units.filter(unit => (unit.status || '').toLowerCase() === 'reserved').length,
    [units]
  );
  const kpiCards = useMemo(() => {
    const totalUnits = units.length;
    const publishedUnits = Math.min(publishedUnitIdsSet.size, totalUnits);
    const marketplaceProgress = totalUnits ? Math.round((publishedUnits / totalUnits) * 100) : 0;
    const safeMarketplaceProgress = Math.max(0, Math.min(marketplaceProgress, 100));

    return [
      {
        id: 'available',
        label: 'Unidades Disponibles',
        value: availableUnitsCount,
        context: 'Listas para asignar viaje hoy',
        variant: availableUnitsCount > 0 ? 'success' : 'muted',
        icon: FiCheckCircle,
      },
      {
        id: 'reserved',
        label: 'En Viaje / Reservadas',
        value: reservedUnitsCount,
        context: 'Generando ingresos actualmente',
        variant: 'info',
        icon: FiTruck,
      },
      {
        id: 'market',
        label: 'Publicadas en Marketplace',
        value: `${safeMarketplaceProgress}%`,
        context: totalUnits
          ? `${publishedUnits} de ${totalUnits} unidades visibles`
          : 'Sin unidades visibles',
        variant: 'warning',
        icon: FiGlobe,
        progressPercent: safeMarketplaceProgress,
      },
    ];
  }, [availableUnitsCount, publishedUnitIdsSet, reservedUnitsCount, units.length]);

  const renderListView = () => {
    return (
      <div className="units-list-layout">
        <section className="units-list-view">
        <div className={`dashboard-kpi-wrapper${kpiCollapsed ? ' is-collapsed' : ''}`}>
          <div className="dashboard-kpi-wrapper__controls">
            <span className="dashboard-kpi-wrapper__label">Indicadores</span>
            <button
              type="button"
              className="dashboard-kpi-toggle"
              onClick={() => setKpiCollapsed(prev => !prev)}
              aria-pressed={!kpiCollapsed}
            >
              {kpiCollapsed ? 'Mostrar' : 'Ocultar'}
            </button>
          </div>
          {!kpiCollapsed ? (
            <div className="units-summary-grid">
              {kpiCards.map(card => {
                const Icon = card.icon;
                const progressPercent =
                  typeof card.progressPercent === 'number'
                    ? Math.max(0, Math.min(card.progressPercent, 100))
                    : null;

                return (
                  <article key={card.id} className={`units-summary-card units-summary-card--${card.variant}`}>
                    <span
                      className={`units-summary-card__icon units-summary-card__icon--${card.variant}`}
                      aria-hidden="true"
                    >
                      {Icon ? <Icon /> : null}
                    </span>
                    <div className="units-summary-card__body">
                      <p className="units-summary-card__label">{card.label}</p>
                      <strong className={`units-summary-card__value units-summary-card__value--${card.variant}`}>
                        {card.value}
                      </strong>
                      <div className="units-summary-card__footer">
                        <span className="units-summary-card__context">{card.context}</span>
                        {progressPercent !== null ? (
                          <div className="units-summary-card__progress" aria-hidden="true">
                            <div className="units-summary-card__progress-track">
                              <span
                                className="units-summary-card__progress-fill"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>

        {success && <div className="form-success">{success}</div>}
        {mode === 'list' && error && <div className="form-error">{error}</div>}

        <div className="units-table-card">
          <div className="units-toolbar">
            <div className="units-toolbar__context">
              <div className="units-toolbar__heading">
                <h2 className="units-toolbar__title">Mis Unidades</h2>
              </div>
              <div className="units-toolbar__search">
                <FiSearch aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Buscar por unidad, folio o estado..."
                  aria-label="Buscar unidades"
                />
              </div>
            </div>
            <div className="units-toolbar__actions">
              <div className="units-view-toggle" role="group" aria-label="Modo de visualizacion de unidades">
                <span className="units-view-toggle__label">Vista</span>
                <div className="units-view-toggle__controls">
                  <button
                    type="button"
                    className={`units-view-toggle__button${listDisplayMode === 'table' ? ' is-active' : ''}`}
                    onClick={() => setListDisplayMode('table')}
                  >
                    Tabla
                  </button>
                  <button
                    type="button"
                    className={`units-view-toggle__button${listDisplayMode === 'garage' ? ' is-active' : ''}`}
                    onClick={() => setListDisplayMode('garage')}
                  >
                    Garage
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="dashboard-action-btn dashboard-action-btn--primary"
                onClick={handleCreateNew}
              >
                Registrar unidad
              </button>
            </div>
          </div>

          <div className="units-table-card__body">
            {listDisplayMode === 'table' ? (
              <>
                <div className="table-scroll-hint" role="note">
                  <span aria-hidden="true" className="table-scroll-hint__icon" />
                  <span className="table-scroll-hint__text">
                    Desliza horizontalmente, haz clic en un encabezado para ordenar (A-Z/Z-A), clic derecho
                    para configurar columnas o arrastra el borde para ajustar el ancho.
                  </span>
                </div>

            <div className="units-table-wrapper">
              <table className="units-table">
                <thead>
                  <tr>
                    {visibleUnitColumns.map(column => {
                      const isSorted = sortConfig.key === column.key;
                      const ariaSort = isSorted
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none';
                      return (
                        <th
                          key={column.key}
                          ref={element => {
                            if (element) {
                              columnHeaderRefs.current[column.key] = element;
                            }
                          }}
                          style={{ minWidth: `${column.width}px`, width: `${column.width}px` }}
                          className="table-column-header"
                          onClick={() => handleColumnSort(column.key)}
                          onContextMenu={event => handleColumnContextMenu(event, column.key)}
                          onKeyDown={event => handleColumnHeaderKeyDown(event, column.key)}
                          tabIndex={0}
                          aria-sort={ariaSort}
                          title="Haz clic para ordenar (A-Z/Z-A). Clic derecho abre la configuracion de columnas."
                        >
                          <span className="table-column-header__label">
                            {column.label}
                            <span
                              className={`table-column-header__sort${
                                isSorted ? ` table-column-header__sort--${sortConfig.direction}` : ''
                              }`}
                              aria-hidden="true"
                            >
                              {isSorted ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                            </span>
                            {isSorted ? (
                              <span className="visually-hidden">
                                {`Ordenado ${sortConfig.direction === 'asc' ? 'ascendente' : 'descendente'}`}
                              </span>
                            ) : null}
                          </span>
                          <span
                            role="presentation"
                            className="table-column-header__resizer"
                            onMouseDown={event => handleColumnResizeMouseDown(event, column)}
                            onTouchStart={event => handleColumnResizeTouchStart(event, column)}
                            aria-hidden="true"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, index) =>
                      renderPlaceholderRow(`loading-${index}`)
                    )
                  ) : (
                    <>
                      {paginatedUnits.map(unit => {
                      const extraAttributes = Array.isArray(unit.extraAttributes)
                        ? unit.extraAttributes
                        : [];
                      const activeEquipment = EQUIPMENT_OPTIONS.filter(
                        option => Boolean(unit?.[option.name])
                      );
                      const customEquipment = Array.isArray(unit.customEquipment)
                        ? unit.customEquipment
                            .map(item => (typeof item === 'string' ? item.trim() : ''))
                            .filter(Boolean)
                        : [];
                      const itemTitle = unit.model || unit.tractorType || 'Unidad sin modelo';
                      const itemSubtitle = unit.tractorType || 'Tipo no especificado';
                      const capacityLine = unit.capacity ? `Capacidad: ${unit.capacity}` : '';
                      const equipmentSummary = [
                        ...activeEquipment.map(option => option.label),
                        ...customEquipment.slice(0, 2),
                        ...extraAttributes.slice(0, 1).map(attribute =>
                          attribute
                            ? `${attribute.name || 'Dato'}: ${attribute.value || 'N/D'}`
                            : ''
                        ),
                      ]
                        .filter(Boolean)
                        .slice(0, 3);
                      const statusValue = unit.status || 'available';
                      const statusLabel = UNIT_STATUS_LABELS[statusValue] || statusValue;

                      const creatorLabel =
                        unit.createdByUser?.name ||
                        unit.createdByUser?.username ||
                        unit.createdByUser?.email ||
                        'Sin registrar';
                      const relativeLabel = formatRelativeTimeFromNow(
                        unit.updated || unit.created,
                        relativeTimeFormatter
                      );
                      const activityRoute = getRouteLabel(unit) || 'Ruta pendiente';
                      const activityTime = relativeLabel || 'Actualizacion reciente';
                      const showPublishAction =
                        statusValue === 'available' && !publishedUnitIdsSet.has(unit.id);
                      const columnContent = {
                        unit: (
                          <div className="units-table-main">
                            <div className="units-table-photo">
                              {unit.photoUrl ? (
                                <img src={unit.photoUrl} alt={itemTitle} />
                              ) : (
                                <span>{(unit.model || unit.tractorType || 'U')[0]}</span>
                              )}
                            </div>
                            <div>
                              <p className="units-table-title">{itemTitle}</p>
                              <p className="units-table-subtitle">{itemSubtitle}</p>
                              {capacityLine ? <p className="units-table-meta">{capacityLine}</p> : null}
                              {equipmentSummary.length ? (
                                <p className="units-table-meta units-table-meta--muted">
                                  {equipmentSummary.join(' · ')}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        ),
                        status: (
                          <span className={`unit-status-chip status-${statusValue}`}>
                            {statusLabel}
                          </span>
                        ),
                        activity: (
                          <div className="units-activity">
                            <p className="units-activity__route">{activityRoute}</p>
                            <span className="units-activity__time">{activityTime}</span>
                          </div>
                        ),
                        createdBy: creatorLabel || 'Sin registrar',
                        updatedAt: (
                          <time dateTime={unit.updated}>
                            {new Date(unit.updated || unit.created).toLocaleDateString()}
                          </time>
                        ),
                        actions: (
                          <div className="units-row-actions">
                            {showPublishAction ? (
                              <button
                                type="button"
                                className="units-row-action units-row-action--primary"
                                onClick={event => {
                                  event.stopPropagation();
                                  handlePublishShortcut(unit);
                                }}
                              >
                                Publicar
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="units-row-action units-row-action--icon"
                              onClick={event => {
                                event.stopPropagation();
                                handleEdit(unit);
                              }}
                            >
                              <FiMoreVertical aria-hidden="true" />
                              <span className="visually-hidden">Editar {itemTitle}</span>
                            </button>
                          </div>
                        ),
                      };

                      return (
                        <tr
                          key={unit.id}
                          className="units-table-row"
                          data-clickable="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => handleEdit(unit)}
                          onKeyDown={event => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleEdit(unit);
                            }
                          }}
                        >
                          {visibleUnitColumns.map(column => (
                            <td
                              key={`${unit.id}-${column.key}`}
                              style={{
                                minWidth: `${column.width}px`,
                                width: `${column.width}px`,
                              }}
                              className={`units-table-cell units-table-cell--${column.key}`}
                            >
                              {columnContent[column.key]}
                            </td>
                          ))}
                    </tr>
                      );
                  })}
                  {showSearchNoResults ? (
                    <tr className="units-table-empty">
                      <td colSpan={Math.max(1, visibleUnitColumns.length)}>
                        <p>
                          No encontramos unidades que coincidan con &quot;{searchQuery.trim()}&quot;.
                        </p>
                      </td>
                    </tr>
                  ) : showEmptyState ? (
                    <tr className="units-table-empty">
                      <td colSpan={Math.max(1, visibleUnitColumns.length)}>
                        <FaStickyNote aria-hidden="true" />
                        <p>Registra tu primera unidad para mostrarla en Flotte.</p>
                      </td>
                    </tr>
                  ) : null}
                </>
              )}
            </tbody>
          </table>
        </div>
              </>
            ) : showSearchNoResults ? (
              <div className="units-garage-empty">
                <p>No encontramos unidades que coincidan con &quot;{searchQuery.trim()}&quot;.</p>
              </div>
            ) : (
              <section className="units-garage-panel" aria-label="Garage de unidades">
                <UnitsGarageGrid units={sortedUnits} onEdit={handleEdit} isLoading={loading} />
              </section>
            )}
          </div>

          {listDisplayMode === 'table' && !showSearchNoResults ? (
            <div className="units-table-card__footer">
              <div className="table-pagination">
                <div className="table-pagination__page-size">
                  <label>
                    Mostrar
                    <select value={pageSize} onChange={handlePageSizeChange}>
                      {PAGE_SIZE_OPTIONS.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    unidades
                  </label>
                </div>
                <div className="table-pagination__controls" role="navigation" aria-label="Paginacion de unidades">
                  <button
                    type="button"
                    onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                    disabled={page === 0}
                  >
                    Anterior
                  </button>
                  <div className="table-pagination__pages">
                    {pageNumbers.map(number => (
                      <button
                        type="button"
                        key={`page-${number}`}
                        className={number === page ? 'active' : ''}
                        aria-current={number === page ? 'page' : undefined}
                        onClick={() => setPage(number)}
                      >
                        {number + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        </section>
      </div>
    );
  };

  const renderFormView = () => {
    const normalizedStatus = STATUS_SEGMENTS.some(segment => segment.value === form.status)
      ? form.status
      : STATUS_SEGMENTS[0].value;

    const generalCard = (
      <section className="units-section units-section--general">
        <header className="units-section__head">
          <div>
            <h3>Informacion general</h3>
            <p>Describe el tipo, modelo y capacidad de la unidad.</p>
          </div>
        </header>
        <div className="units-field-grid units-field-grid--two">
          <div className="empresa-form-group">
            <label htmlFor="unit-tractor">Tipo de unidad</label>
            <input
              id="unit-tractor"
              name="tractorType"
              type="text"
              list="unit-type-suggestions"
              value={form.tractorType}
              onChange={handleFieldChange}
              placeholder="Ej. Tractocamion, Van, Refrigerado..."
              required
            />
            <datalist id="unit-type-suggestions">
              {UNIT_TYPES.map(option => (
                <option key={option.value} value={option.label} />
              ))}
            </datalist>
            <p className="units-field-hint units-field-hint--emphasis">
              <FiInfo aria-hidden="true" />
              <span>Usa una descripcion sencilla que tus clientes reconozcan de inmediato.</span>
            </p>
          </div>
          <div className="empresa-form-group">
            <label htmlFor="unit-model">Modelo</label>
            <div className="units-input units-input--icon">
              <FiHash aria-hidden="true" />
              <input
                id="unit-model"
                name="model"
                type="text"
                value={form.model}
                onChange={handleFieldChange}
                placeholder="Ej. Freightliner Cascadia 2024"
                required
              />
            </div>
            <p className="units-field-hint units-field-hint--emphasis">
              <FiInfo aria-hidden="true" />
              <span>Incluye marca, linea y anio para dar contexto tecnico.</span>
            </p>
          </div>
          <div className="empresa-form-group">
            <label htmlFor="unit-capacity">Capacidad</label>
            <div className="units-input units-input--icon">
              <FiPackage aria-hidden="true" />
              <input
                id="unit-capacity"
                name="capacity"
                type="text"
                value={form.capacity}
                onChange={handleFieldChange}
                placeholder="Ej. 53 pies, 26 toneladas"
              />
            </div>
            <p className="units-field-hint units-field-hint--emphasis">
              <FiInfo aria-hidden="true" />
              <span>Especifica capacidades utiles como volumen y peso maximo.</span>
            </p>
          </div>
          <div className="empresa-form-group">
            <label htmlFor="unit-economic">Numero economico</label>
            <input
              id="unit-economic"
              name="economicNumber"
              type="text"
              value={form.economicNumber}
              onChange={handleFieldChange}
              placeholder="Ej. TR-2045"
            />
            <p className="units-field-hint units-field-hint--emphasis">
              <FiInfo aria-hidden="true" />
              <span>Dato interno para tu equipo; acelera el seguimiento.</span>
            </p>
          </div>
          <div className="empresa-form-group units-status-group">
            <label>Estado operativo</label>
            <div className="units-status-control" role="radiogroup" aria-label="Estado operativo">
              {STATUS_SEGMENTS.map(option => {
                const Icon = option.Icon;
                const isActive = normalizedStatus === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    className={`units-status-option units-status-option--${option.tone}${
                      isActive ? ' is-active' : ''
                    }`}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    <span className="units-status-option__icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <div className="units-status-option__body">
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="units-field-hint units-field-hint--emphasis">
              <FiInfo aria-hidden="true" />
              <span>Ajusta el estado para pausar reservas o marcar mantenimiento.</span>
            </p>
          </div>
        </div>
      </section>
    );

    const pricingCard = (
      <section className="units-section units-section--pricing">
        <header className="units-section__head">
          <div>
            <h3>Tarifas y destinos</h3>
            <p>Define precio base de la unidad y agrega corredores con tarifa dedicada.</p>
          </div>
        </header>
        <div className="units-pricing-grid">
          <div className="empresa-form-group">
            <label htmlFor="unit-currency">Moneda</label>
            <select
              id="unit-currency"
              name="currency"
              className="units-select"
              value={form.currency}
              onChange={handleFieldChange}
            >
              {CURRENCY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="units-field-hint">
              Usa MXN para rutas nacionales o USD para cruces internacionales.
            </p>
          </div>
          <div className="empresa-form-group">
            <label htmlFor="unit-base-price">Precio base</label>
            <div className="units-price-input">
              <span className="units-price-input__badge">{form.currency}</span>
              <input
                id="unit-base-price"
                type="text"
                inputMode="decimal"
                value={form.basePrice || ''}
                onChange={event => handleBasePriceChange(event.target.value)}
                placeholder="0.00"
              />
            </div>
            <p className="units-field-hint">Ejemplo: 19,000.00 {form.currency}</p>
          </div>
        </div>
        <div className="units-price-destinations">
          <div className="units-price-destinations__head">
            <div className="units-price-destinations__intro">
              <strong>Destinos</strong>
              <p>Personaliza el precio segun la plaza o unlocode.</p>
            </div>
            <button
              type="button"
              className="units-price-destinations__add"
              onClick={handleDestinationAdd}
              disabled={saving}
            >
              Agregar destino
            </button>
          </div>
          {(form.destinationPrices || []).length === 0 ? (
            <p className="units-price-empty">
              Aun no agregas destinos. Suma tus corredores clave como Guanajuato (MXGTO) o San Luis
              Potosi (MXSLP).
            </p>
          ) : (
            <div className="units-price-destination-list">
              {form.destinationPrices.map(entry => (
                <div className="units-price-destination-row" key={entry.id}>
                  <label>
                    Destino (UN/LOCODE)
                    <LocationAutocompleteInput
                      name={`destination-${entry.id}`}
                      type="text"
                      value={
                        typeof entry.destinationInput === 'string' && entry.destinationInput.trim()
                          ? entry.destinationInput
                          : getDestinationLabel(entry.destinationCode)
                      }
                      onChange={event => handleDestinationInputChange(entry.id, event.target.value)}
                      placeholder="Escribe para buscar un UN/LOCODE"
                      className="units-destination-input"
                      autoComplete="off"
                    />
                  </label>
                  <label>
                    Tarifa destino
                    <div className="units-price-input">
                      <span className="units-price-input__badge">{form.currency}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={entry.price || ''}
                        onChange={event =>
                          handleDestinationPriceChange(entry.id, event.target.value)
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </label>
                  <button
                    type="button"
                    className="units-price-remove"
                    onClick={() => handleDestinationRemove(entry.id)}
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );

    const equipmentCard = (
      <section className="units-section units-section--equipment">
        <header className="units-section__head">
          <div>
            <h3>Equipamiento</h3>
            <p>Marca las caracteristicas que ofrece la unidad.</p>
          </div>
        </header>
        <div className="units-equipment-grid">
          {EQUIPMENT_OPTIONS.map(item => {
            const Icon = EQUIPMENT_ICON_MAP[item.name] || LuSparkles;
            const isActive = Boolean(form[item.name]);
            return (
              <label
                key={item.name}
                className={`units-equipment-card${isActive ? ' active' : ''}`}
              >
                <input
                  type="checkbox"
                  name={item.name}
                  checked={form[item.name]}
                  onChange={handleCheckboxChange}
                />
                <span className="units-equipment-card__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="units-equipment-card__label">{item.label}</span>
              </label>
            );
          })}
        </div>
        <p className="units-field-hint">
          Selecciona solo los atributos disponibles para mantener expectativas claras.
        </p>
        <div className="units-custom-equipment">
          <label htmlFor="custom-equipment-input">Agrega mas caracteristicas</label>
          <div className="units-custom-input">
            <input
              id="custom-equipment-input"
              type="text"
              value={customEquipmentInput}
              onChange={event => setCustomEquipmentInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleCustomEquipmentAdd();
                }
              }}
              placeholder="Ej. Sensor de temperatura, Track & Trace..."
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCustomEquipmentAdd}
              disabled={
                saving || customEquipmentLimitReached || !customEquipmentInput.trim()
              }
            >
              Agregar
            </button>
          </div>
          <p className="units-custom-helper">
            Puedes agregar hasta <strong>{MAX_CUSTOM_EQUIPMENT}</strong> opciones personalizadas.
            {customEquipmentLimitReached ? <span> Ya alcanzaste el limite.</span> : null}
          </p>
          <p className="units-field-hint">
            Usa frases cortas y especificas para resaltar diferenciales clave.
          </p>
          {form.customEquipment.length > 0 && (
            <div className="units-custom-list">
              {form.customEquipment.map((label, index) => (
                <span key={`${label}-${index}`} className="units-custom-chip">
                  {label}
                  <button
                    type="button"
                    aria-label={`Quitar ${label}`}
                    onClick={() => handleCustomEquipmentRemove(index)}
                  >
                    A-
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>
    );

    const photoCard = (
      <section className="units-section units-section--photo">
        <header className="units-section__head">
          <div>
            <h3>Foto de la unidad</h3>
            <p>Agrega una imagen para que los clientes visualicen la unidad.</p>
          </div>
        </header>
        <p className="units-photo-note">PNG o JPG, maximo 5 MB.</p>
        {hasPreview ? (
          <div className="units-photo-preview">
            <img src={form.photoPreview} alt="Unidad" />
          </div>
        ) : (
          <div className="units-photo-dropzone dashed">
            <span>Selecciona una imagen para mostrarla aqui.</span>
          </div>
        )}
        <div className="units-photo-actions units-photo-actions--footer">
          <label className="btn btn-secondary">
            Seleccionar
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </label>
          {hasPreview && (
            <button type="button" className="btn-action" onClick={handlePhotoRemove}>
              Quitar foto
            </button>
          )}
        </div>
        <p className="units-field-hint">
          Elige una foto nitida, con buena iluminacion y sin datos confidenciales.
        </p>
      </section>
    );

    const availabilityCard = (
      <section className="units-section units-section--availability">
        <header className="units-section__head">
          <div>
            <h3>Disponibilidad</h3>
            <p>Selecciona uno o varios rangos de fechas en los que la unidad está disponible.</p>
          </div>
        </header>
        <div className="units-availability">
          <div className="units-availability-summary">
            <div>
              <strong>{form.availabilityRanges.length}</strong>{' '}
              {form.availabilityRanges.length === 1 ? 'rango seleccionado' : 'rangos seleccionados'}
            </div>
          </div>
          <p className="units-availability-hint">
            Selecciona fechas en el calendario para crear nuevos rangos. Arrastra los bloques para ajustarlos y usa la lista o haz clic sobre un bloque para eliminarlo.
          </p>
          <div className="units-availability-calendar">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale="es"
              locales={[esLocale]}
              height="auto"
              contentHeight="auto"
              aspectRatio={1.6}
              expandRows
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: '',
              }}
              buttonText={{
                today: 'Hoy',
              }}
              selectable
              selectMirror
              dayMaxEvents
              events={availabilityEvents}
              select={handleAvailabilitySelect}
              eventClick={handleAvailabilityEventClick}
              eventDrop={handleAvailabilityEventUpdate}
              eventResize={handleAvailabilityEventUpdate}
              editable
              eventResizableFromStart
              eventOverlap={false}
              eventDurationEditable
            />
          </div>
          {form.availabilityRanges.length > 0 ? (
            <ul className="units-availability-list">
              {form.availabilityRanges.map((range, index) => (
                <li key={`${range.start}-${range.end}-${index}`} className="units-availability-item">
                  <div className="units-availability-copy">
                    <strong>{`Rango ${index + 1}`}</strong>
                    <span>{formatAvailabilityRange(range) || 'Sin fecha'}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-action"
                    onClick={() => handleAvailabilityRemove(index)}
                    disabled={saving}
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="units-availability-empty">Aun no hay rangos seleccionados.</p>
          )}
        </div>
      </section>
    );

    const renderDivider = label => (
      <div className="units-section-divider" role="presentation">
        <span>{label}</span>
      </div>
    );

    const tabs = [
      {
        id: 'general',
        label: 'Informacion general',
        description: 'Captura los datos basicos y capacidades.',
        content: (
          <>
            {renderDivider('Datos tecnicos')}
            <div className="units-step-stack">{generalCard}</div>
          </>
        ),
      },
      {
        id: 'equipment',
        label: 'Equipamiento',
        description: 'Marca caracteristicas, accesorios y notas internas.',
        content: (
          <>
            {renderDivider('Equipamiento')}
            <div className="units-step-stack">{equipmentCard}</div>
          </>
        ),
      },
      {
        id: 'media',
        label: 'Material visual',
        description: 'Carga fotos claras para mostrar la unidad.',
        content: (
          <>
            {renderDivider('Material visual')}
            <div className="units-step-stack">{photoCard}</div>
          </>
        ),
      },
      {
        id: 'pricing',
        label: 'Tarifas y destinos',
        description: 'Configura moneda, precio base y rutas clave.',
        content: (
          <>
            {renderDivider('Tarifas y destinos')}
            <div className="units-step-stack">{pricingCard}</div>
          </>
        ),
      },
      {
        id: 'availability',
        label: 'Disponibilidad',
        description: 'Administra rangos y controla cuando se muestra la unidad.',
        content: (
          <>
            {renderDivider('Disponibilidad')}
            <div className="units-step-stack">{availabilityCard}</div>
          </>
        ),
      },
    ];

    const rawActiveTabIndex = tabs.findIndex(tab => tab.id === activeTab);
    const activeTabIndex = rawActiveTabIndex === -1 ? 0 : rawActiveTabIndex;
    const isLastTab = activeTabIndex >= tabs.length - 1;
    const handleNextStep = () => {
      if (isLastTab) return;
      setActiveTab(tabs[activeTabIndex + 1].id);
    };

    return (
      <div className="units-form-layout">
        <section className="units-form-view">
          <div className="units-form-header-shell">
            <div className="units-form-header-shell__content">
              <div className="units-form-header-content">
                <p className="units-form-header-eyebrow">Gestion de unidades</p>
                <h2>{form.id ? 'Editar unidad' : 'Registrar unidad'}</h2>
                <span>
                  Completa la informacion tecnica de la unidad para compartirla con tus clientes.
                </span>
              </div>
              <div
                className="units-form-header-actions"
                role="toolbar"
                aria-label="Acciones principales del formulario"
              >
                <button
                  type="button"
                  className="units-form-action-link"
                  onClick={() => {
                    resetForm();
                    setMode('list');
                  }}
                >
                  Regresar
                </button>
                <button
                  type="submit"
                  form="units-form"
                  className="units-form-action-btn"
                  disabled={saving}
                >
                  {saving
                    ? 'Guardando...'
                    : form.id
                    ? 'Actualizar unidad'
                    : 'Guardar unidad'}
                </button>
              </div>
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <form id="units-form" onSubmit={handleSubmit} className="units-form">
            <div className="units-form-body">
              <div className="units-stepper-wrapper">
                <div className="units-stepper">
                  <div className="units-stepper__tabs" role="tablist" aria-label="Pasos para registrar la unidad">
                  {tabs.map((tab, index) => {
                    const isActive = activeTab === tab.id;
                    const progressPercent = Math.round((stepProgress[tab.id] || 0) * 100);
                    return (
                      <button
                        key={tab.id}
                        id={`units-tab-${tab.id}`}
                        type="button"
                        role="tab"
                        className={`units-stepper-button${isActive ? ' active' : ''}`}
                        aria-selected={isActive}
                        aria-controls={`units-panel-${tab.id}`}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        <span
                          className="units-stepper-index"
                          style={{ '--progress-value': `${progressPercent}` }}
                        >
                          {index + 1}
                        </span>
                        <span className="units-stepper-copy">
                          <strong>{tab.label}</strong>
                          {tab.description ? <span>{tab.description}</span> : null}
                        </span>
                      </button>
                    );
                  })}
                  </div>
                  <div className="units-stepper__footer">
                    <button
                      type="button"
                      className="units-stepper-next"
                      onClick={handleNextStep}
                      disabled={isLastTab}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
              <div className="units-step-panel">
                {tabs.map(tab =>
                  tab.id === activeTab ? (
                    <div
                      key={tab.id}
                      id={`units-panel-${tab.id}`}
                      role="tabpanel"
                      aria-labelledby={`units-tab-${tab.id}`}
                      className="units-step-content"
                    >
                      {tab.content}
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </form>
          {form.id ? (
            <div className="units-form-footer">
              <button
                type="button"
                className="units-form-danger"
                onClick={() => handleDelete(form.id)}
                disabled={saving}
              >
                Eliminar unidad
              </button>
            </div>
          ) : null}
        </section>
      </div>
    );
  };
  return (
    <div className="units-shell">
      {mode === 'list' ? renderListView() : renderFormView()}
      <TableColumnMenu
        anchorElement={columnHeaderRefs.current[activeColumnMenu] || null}
        activeColumnKey={activeColumnMenu}
        columns={unitColumns}
        onClose={() => setActiveColumnMenu('')}
        onMove={moveUnitColumn}
        onToggle={toggleUnitColumn}
        onWidthChange={setUnitColumnWidth}
        onReset={resetUnitColumns}
      />
    </div>
  );
}
